import React, {
  useState,
  useEffect,
  useContext,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaPrint,
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaBox,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import { Boxes } from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const StockSummaryReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(ThemeContext);

  const currentSkin =
    getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     LOCAL STORAGE VALUES
  ========================================================= */

  const finYearId =
    localStorage.getItem("finYearId") || "";

  const finYear =
    localStorage.getItem("finYear") || "";

  const storedBranchId =
    localStorage.getItem("branchId") || "";

  const storedBranchType =
    localStorage.getItem("branchType") || "";

  const userId =
    localStorage.getItem("userId") || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders = {
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": storedBranchId,
    "x-branch": storedBranchType,
    "x-userid": userId,
  };

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showForm,
    setShowForm,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId || "8"
  );

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    itemCode,
    setItemCode,
  ] = useState("");

  const [
    itemName,
    setItemName,
  ] = useState("");

  const [
    level,
    setLevel,
  ] = useState("Select");

  const [
    stock,
    setStock,
  ] = useState("Select");

  /* =========================================================
     DROPDOWN LISTS
  ========================================================= */

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    categoryList,
    setCategoryList,
  ] = useState([]);

  const [
    subCategoryList,
    setSubCategoryList,
  ] = useState([]);

  const [
    innerSubCategoryList,
    setInnerSubCategoryList,
  ] = useState([]);

  const [
    deepInnerSubCategoryList,
    setDeepInnerSubCategoryList,
  ] = useState([]);

  /* =========================================================
     SELECTED CATEGORY VALUES
  ========================================================= */

  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState("");

  const [
    selectedSubCategory,
    setSelectedSubCategory,
  ] = useState("");

  const [
    selectedInnerSubCategory,
    setSelectedInnerSubCategory,
  ] = useState("");

  const [
    selectedDeepInnerSubCategory,
    setSelectedDeepInnerSubCategory,
  ] = useState("");

  /* =========================================================
     RESULT DATA
  ========================================================= */

  const [
    data,
    setData,
  ] = useState([]);

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  const pageSize = 10;

  /* =========================================================
     DESIGN CLASSES
     SAME AS FIRST PAGE TEMPLATE
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     INITIAL BRANCH + CATEGORY LOAD
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/fillddl_itemRpt",
        {
          params: {
            branchid:
              branchId ||
              storedBranchId ||
              8,
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        setBranchList(
          res.data.branchList || []
        );

        setCategoryList(
          res.data.categoryList || []
        );
      })
      .catch((err) => {
        console.error(
          "Initial dropdown fetch failed",
          err
        );

        toast.error(
          "Failed to load branches/categories"
        );
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================================================
     BRANCH CHANGE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    if (!branchId) {
      return;
    }

    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/fillddl_itemRpt",
        {
          params: {
            branchid:
              branchId,
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        setBranchList(
          res.data.branchList || []
        );

        setCategoryList(
          res.data.categoryList || []
        );
      })
      .catch((err) => {
        console.error(
          "Branch/category fetch failed",
          err
        );

        toast.error(
          "Failed to load categories"
        );
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  /* =========================================================
     FETCH SUB CATEGORIES
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const fetchSubCategories = (
    categoryId
  ) => {
    if (!categoryId) {
      setSubCategoryList([]);
      setInnerSubCategoryList([]);
      setDeepInnerSubCategoryList([]);

      setSelectedSubCategory("");
      setSelectedInnerSubCategory("");
      setSelectedDeepInnerSubCategory("");

      return;
    }

    axios
      .get(
        `https://nissi-india.com/api/subcategory_rpt/${categoryId}`,
        {
          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        setSubCategoryList(
          res.data.subCategoryList || []
        );

        setInnerSubCategoryList([]);
        setDeepInnerSubCategoryList([]);

        setSelectedSubCategory("");
        setSelectedInnerSubCategory("");
        setSelectedDeepInnerSubCategory("");
      })
      .catch((err) => {
        console.error(
          "Subcategory fetch failed",
          err
        );

        toast.error(
          "Failed to load sub categories"
        );
      });
  };

  /* =========================================================
     FETCH INNER SUB CATEGORIES
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const fetchInnerSubCategories = (
    subCategoryId
  ) => {
    if (!subCategoryId) {
      setInnerSubCategoryList([]);
      setDeepInnerSubCategoryList([]);

      setSelectedInnerSubCategory("");
      setSelectedDeepInnerSubCategory("");

      return;
    }

    axios
      .get(
        `https://nissi-india.com/api/innersubcategory_rpt/${subCategoryId}`,
        {
          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        setInnerSubCategoryList(
          res.data.innerSubCategoryList ||
            []
        );

        setDeepInnerSubCategoryList([]);

        setSelectedInnerSubCategory("");
        setSelectedDeepInnerSubCategory("");
      })
      .catch((err) => {
        console.error(
          "Inner subcategory fetch failed",
          err
        );

        toast.error(
          "Failed to load inner sub categories"
        );
      });
  };

  /* =========================================================
     FETCH DEEP INNER SUB CATEGORIES
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const fetchDeepInnerSubCategories = (
    innerSubCategoryId
  ) => {
    if (!innerSubCategoryId) {
      setDeepInnerSubCategoryList([]);

      setSelectedDeepInnerSubCategory("");

      return;
    }

    axios
      .get(
        `https://nissi-india.com/api/deepinnersubcategory_rpt/${innerSubCategoryId}`,
        {
          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        setDeepInnerSubCategoryList(
          res.data
            .deepInnerSubCategoryList ||
            []
        );
      })
      .catch((err) => {
        console.error(
          "Deep inner subcategory fetch failed",
          err
        );

        toast.error(
          "Failed to load deep inner sub categories"
        );
      });
  };

  /* =========================================================
     DATE FORMAT
  ========================================================= */

  const formatDate = (
    dateStr
  ) => {
    if (!dateStr) {
      return "";
    }

    const [
      year,
      month,
      day,
    ] = dateStr.split("-");

    return `${day}/${month}/${year}`;
  };

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    setLoading(true);
    setCurrentPage(1);

    const params = {
      sessionBranchId:
        branchId,

      ddl_Branch:
        String(
          branchId ||
            "Select"
        ),

      ddl_category:
        selectedCategory ||
        "Select",

      ddl_subCategory:
        selectedSubCategory ||
        "Select",

      ddl_innersubcategory:
        selectedInnerSubCategory ||
        "Select",

      ddl_deepinnersubcategory:
        selectedDeepInnerSubCategory ||
        "Select",

      txt_item:
        itemCode || "",

      txt_itemname:
        itemName || "",

      txt_FromDate:
        formatDate(
          fromDate
        ) || "",

      txt_ToDate:
        formatDate(
          toDate
        ) || "",

      ddl_level:
        level ||
        "Select",

      ddl_interval:
        "Select",

      ddl_Stockwise:
        stock ||
        "Select",

      financeyearstartdate:
        localStorage.getItem(
          "financeyearstartdate"
        ) || "",
    };

    axios
      .post(
        "https://nissi-india.com/api/stock_summaryRpt",
        params,
        {
          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const rows =
          res.data.rows ||
          res.data.recordset ||
          res.data
            .recordsets?.[0] ||
          [];

        setData(rows);

        toast.success(
          `Loaded ${rows.length} records`
        );
      })
      .catch((err) => {
        console.error(
          "Stock summary fetch failed",
          err
        );

        toast.error(
          "Failed to fetch stock summary"
        );

        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = () => {
    const params = {
      sessionBranchId:
        branchId,

      ddl_Branch:
        String(
          branchId ||
            "Select"
        ),

      ddl_category:
        selectedCategory ||
        "Select",

      ddl_subCategory:
        selectedSubCategory ||
        "Select",

      ddl_innersubcategory:
        selectedInnerSubCategory ||
        "Select",

      ddl_deepinnersubcategory:
        selectedDeepInnerSubCategory ||
        "Select",

      txt_item:
        itemCode || "",

      txt_itemname:
        itemName || "",

      txt_FromDate:
        formatDate(
          fromDate
        ) || "",

      txt_ToDate:
        formatDate(
          toDate
        ) || "",

      ddl_level:
        level ||
        "Select",

      ddl_interval:
        "Select",

      ddl_Stockwise:
        stock ||
        "Select",

      financeyearstartdate:
        localStorage.getItem(
          "financeyearstartdate"
        ) || "",
    };

    const query =
      new URLSearchParams(
        params
      ).toString();

    window.open(
      `/inventory/StockSummaryReportPrint?${query}`,
      "_blank"
    );
  };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters = () => {
    setBranchId(
      storedBranchId || "8"
    );

    setFromDate("");
    setToDate("");

    setItemCode("");
    setItemName("");

    setLevel("Select");
    setStock("Select");

    setSelectedCategory("");
    setSelectedSubCategory("");
    setSelectedInnerSubCategory("");
    setSelectedDeepInnerSubCategory("");

    setSubCategoryList([]);
    setInnerSubCategoryList([]);
    setDeepInnerSubCategoryList([]);

    setData([]);
    setCurrentPage(1);
  };

  /* =========================================================
     CURRENCY
  ========================================================= */

  const formatCurrency = (
    amount
  ) => {
    return new Intl.NumberFormat(
      "en-IN",
      {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    ).format(
      amount || 0
    );
  };

  /* =========================================================
     NUMBER
  ========================================================= */

  const formatNumber = (
    value
  ) => {
    return value
      ? parseFloat(
          value
        ).toLocaleString(
          "en-IN",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        )
      : "0.00";
  };

  /* =========================================================
     PAGINATION
  ========================================================= */

  const totalPages = Math.max(
    1,
    Math.ceil(data.length / pageSize)
  );

  const startIndex =
    (currentPage - 1) * pageSize;

  const pagedData = data.slice(
    startIndex,
    startIndex + pageSize
  );

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Stock Summary Report
          </span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* =================================================
              FORM HEADER
          ================================================= */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >
            {/* LEFT */}

            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Boxes className="w-5 h-5 text-white" />
              </div>

              <div>
                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Stock Summary Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Comprehensive overview of inventory stock levels and values
                </p>
              </div>
            </div>

            {/* RIGHT ACTIONS */}

            <div className="flex flex-wrap items-center gap-3">
              {/*
                EXPORT:
                Your original Stock Summary page contains an Export
                button but no export handler/onClick implementation.
                Therefore the button is preserved without inventing
                new export behaviour.
              */}

              <button
                type="button"
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload size={14} />
                Export
              </button>

              {/* SHOW / HIDE */}

              <button
                type="button"
                onClick={() =>
                  setShowForm(
                    !showForm
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter size={14} />

                {showForm
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* =================================================
              FORM BODY
          ================================================= */}

          {showForm && (
            <div className="p-6">
              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* FROM DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* ITEM CODE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Code
                  </label>

                  <input
                    type="text"
                    value={
                      itemCode
                    }
                    onChange={(e) =>
                      setItemCode(
                        e.target.value
                      )
                    }
                    placeholder="Enter Item Code"
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* ITEM NAME */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Name
                  </label>

                  <input
                    type="text"
                    value={
                      itemName
                    }
                    onChange={(e) =>
                      setItemName(
                        e.target.value
                      )
                    }
                    placeholder="Enter Item Name"
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* LEVEL */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Level
                  </label>

                  <select
                    value={
                      level
                    }
                    onChange={(e) =>
                      setLevel(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="Select">
                      Select
                    </option>

                    <option value="high">
                      High
                    </option>

                    <option value="low">
                      Low
                    </option>
                  </select>
                </div>

                {/* STOCK */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Stock
                  </label>

                  <select
                    value={
                      stock
                    }
                    onChange={(e) =>
                      setStock(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="Select">
                      Select
                    </option>

                    <option value="ZeroStock">
                      Zero Stock
                    </option>

                    <option value="NonZeroStock">
                      Non Zero Stock
                    </option>
                  </select>
                </div>

                {/* BRANCH */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch
                  </label>

                  <select
                    value={
                      branchId
                    }
                    onChange={(e) =>
                      setBranchId(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Branch
                    </option>

                    {branchList.map(
                      (b) => (
                        <option
                          key={
                            b.fld_Branchid ||
                            b.id
                          }
                          value={
                            b.fld_Branchid ||
                            b.id
                          }
                        >
                          {b.fld_BranchName ||
                            b.name}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* CATEGORY */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Category
                  </label>

                  <select
                    value={
                      selectedCategory
                    }
                    onChange={(e) => {
                      setSelectedCategory(
                        e.target.value
                      );

                      fetchSubCategories(
                        e.target.value
                      );
                    }}
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Category
                    </option>

                    {categoryList.map(
                      (c) => (
                        <option
                          key={
                            c.fld_itemcategoryid ||
                            c.id
                          }
                          value={
                            c.fld_itemcategoryid ||
                            c.id
                          }
                        >
                          {c.fld_itemcategory_name ||
                            c.name}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* SUB CATEGORY */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Sub Category
                  </label>

                  <select
                    value={
                      selectedSubCategory
                    }
                    onChange={(e) => {
                      setSelectedSubCategory(
                        e.target.value
                      );

                      fetchInnerSubCategories(
                        e.target.value
                      );
                    }}
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Sub Category
                    </option>

                    {subCategoryList.map(
                      (sc) => (
                        <option
                          key={
                            sc.fld_itemsubCategoryid
                          }
                          value={
                            sc.fld_itemsubCategoryid
                          }
                        >
                          {
                            sc.fld_itemsubCategoryName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* INNER SUB CATEGORY */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Inner Sub Category
                  </label>

                  <select
                    value={
                      selectedInnerSubCategory
                    }
                    onChange={(e) => {
                      setSelectedInnerSubCategory(
                        e.target.value
                      );

                      fetchDeepInnerSubCategories(
                        e.target.value
                      );
                    }}
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Inner Sub Category
                    </option>

                    {innerSubCategoryList.map(
                      (isc) => (
                        <option
                          key={
                            isc.fld_innersubCategoryid
                          }
                          value={
                            isc.fld_innersubCategoryid
                          }
                        >
                          {
                            isc.fld_innersubCategoryName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* DEEP INNER SUB CATEGORY */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Deep Inner Sub Category
                  </label>

                  <select
                    value={
                      selectedDeepInnerSubCategory
                    }
                    onChange={(e) =>
                      setSelectedDeepInnerSubCategory(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Deep Inner Sub Category
                    </option>

                    {deepInnerSubCategoryList.map(
                      (disc) => (
                        <option
                          key={
                            disc.fld_deepinnersubCategoryid
                          }
                          value={
                            disc.fld_deepinnersubCategoryid
                          }
                        >
                          {
                            disc.fld_deepinnersubCategoryName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTIONS
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes size={14} />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`
                    inline-flex
                    items-center
                    gap-2
                    px-4
                    py-2.5
                    text-sm
                    font-semibold
                    rounded-xl
                    transition
                    ${
                      isDark
                        ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }
                  `}
                >
                  <FaPrint size={14} />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
            SAME AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* RESULTS HEADER */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Stock Summary Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {data.length > 0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* LOADING */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading stock summary...
              </p>
            </div>
          ) : data.length > 0 ? (
            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Branch Name
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Item Name
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Item Code
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Location
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      UOM
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Price
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Closing Stock
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Amount
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Report
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {pagedData.map(
                    (po, index) => (
                      <tr
                        key={
                          po.fld_itemid ||
                          index
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        {/* SERIAL */}

                        <td className="px-4 py-3 font-medium">
                          {startIndex + index + 1}
                        </td>

                        {/* BRANCH */}

                        <td className="px-4 py-3 font-medium">
                          {po.fld_branchName ||
                            po.fld_branchname ||
                            "-"}
                        </td>

                        {/* ITEM NAME */}

                        <td className="px-4 py-3">
                          {po.fld_itemname ||
                            "-"}
                        </td>

                        {/* ITEM CODE */}

                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => {
                              window.open(
                                `/inventory/StockItemDetails?id=${po.fld_itemid}`,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold transition ${
                              isDark
                                ? "bg-gray-700 text-blue-300 hover:bg-gray-600"
                                : `bg-${baseColor}-100 text-${baseColor}-800 hover:bg-${baseColor}-200`
                            }`}
                          >
                            {po.fld_itemcode ||
                              "-"}
                          </button>
                        </td>

                        {/* LOCATION */}

                        <td className="px-4 py-3">
                          {po.fld_location ||
                            "-"}
                        </td>

                        {/* UOM */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {po.fld_uomname ||
                            "-"}
                        </td>

                        {/* PRICE */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              window.open(
                                `/inventory/PriceStockDetails?id=${po.fld_itemid}`,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                            className={`font-semibold hover:underline ${
                              isDark
                                ? "text-blue-300"
                                : "text-blue-600"
                            }`}
                          >
                            {formatCurrency(
  po.closingprice
)}
                          </button>
                        </td>

                        {/* CLOSING STOCK */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              window.open(
                                `/inventory/ItemStockDetails?id=${po.fld_itemid}`,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                            className={`font-semibold hover:underline ${
                              isDark
                                ? "text-blue-300"
                                : "text-blue-600"
                            }`}
                          >
                           {formatNumber(
  po.closingQty
)}
                          </button>
                        </td>

                        {/* AMOUNT */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                        {formatCurrency(
  po.closingvalue
)}
                        </td>

                        {/* REPORT */}

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => {
                              window.open(
                                `/inventory/ItemTransactionReport?id=${po.fld_itemid}`,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                            className={`p-1.5 rounded-full transition ${
                              isDark
                                ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                            }`}
                            title="View Transaction Report"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">
              <FaBox
                size={40}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No stock records found
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}

          {/* PAGINATION */}

          {!loading && data.length > pageSize && (
            <div
              className={`flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t ${
                isDark
                  ? "border-slate-700/50"
                  : "border-slate-200"
              }`}
            >
              <span
                className={`text-xs ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-600"
                }`}
              >
                Showing {startIndex + 1}–
                {Math.min(
                  startIndex + pageSize,
                  data.length
                )}{" "}
                of {data.length} records
              </span>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.max(1, p - 1)
                    )
                  }
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border text-xs transition disabled:opacity-40 ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaChevronLeft size={12} />
                </button>

                {Array.from(
                  { length: totalPages },
                  (_, i) => i + 1
                )
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === totalPages ||
                      Math.abs(p - currentPage) <= 1
                  )
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && p - arr[i - 1] > 1 && (
                        <span className="px-1 text-slate-400">
                          …
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={() =>
                          setCurrentPage(p)
                        }
                        className={`min-w-[32px] px-2 py-1.5 rounded-lg text-xs font-semibold transition ${
                          p === currentPage
                            ? `bg-${baseColor}-600 text-white`
                            : isDark
                            ? "text-slate-300 hover:bg-slate-800"
                            : "text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}

                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(totalPages, p + 1)
                    )
                  }
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border text-xs transition disabled:opacity-40 ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaChevronRight size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default StockSummaryReport;